import {
  SPECIAL_CATEGORY_MIN_RADIUS_KM, MIN_ADS, MAX_ADS, HEADLINE_MAX, AUTOFILL_MAX, LOCKED_DEMOGRAPHIC_CATEGORIES, LOCKED_AGE,
  MIN_CAROUSEL_CARDS, SECTIONS,
} from './constants';

export const isLockedCategory = (category) => LOCKED_DEMOGRAPHIC_CATEGORIES.includes(category);

const today = () => new Date().toISOString().split('T')[0];

const adMediaErrors = (ad, label) => {
  if (ad.mediaAssetId) return [];
  if (ad.format === 'CAROUSEL') {
    const errs = [];
    if (ad.cards.length < MIN_CAROUSEL_CARDS) errs.push(`${label}: a carousel needs at least ${MIN_CAROUSEL_CARDS} cards.`);
    ad.cards.forEach((c, i) => {
      if (!c.file) errs.push(`${label}: card ${i + 1} has no image.`);
      else if (!c.ratio) errs.push(`${label}: card ${i + 1} is not 1:1, 4:5 or 9:16.`);
      if (!c.headline.trim()) errs.push(`${label}: card ${i + 1} is missing a headline.`);
    });
    const ratios = new Set(ad.cards.map((c) => c.ratio).filter(Boolean));
    if (ratios.size > 1) errs.push(`${label}: all carousel cards must be the same shape.`);
    return errs;
  }
  if (!ad.file) return [`${label}: add ${ad.format === 'VIDEO' ? 'a video' : 'an image'}.`];
  if (!ad.ratio) return [`${label}: the file must be 1:1, 4:5 or 9:16.`];
  return [];
};

export const adErrors = (ad, label = ad.name || 'Ad') => {
  const errs = [];
  if (!ad.name.trim()) errs.push(`${label}: add an ad name.`);
  errs.push(...adMediaErrors(ad, label));
  if (!ad.primaryText.trim()) errs.push(`${label}: add the primary text.`);
  if (!ad.headline.trim()) errs.push(`${label}: add a headline.`);
  else if (ad.headline.length > HEADLINE_MAX) errs.push(`${label}: the headline is over ${HEADLINE_MAX} characters.`);
  return errs;
};

// ctx: { checks, minDailyBudget (major units), currencySymbol }
const VALIDATORS = {
  account: (f, ctx) => {
    const errs = [];
    if (!f.account.adAccountId) errs.push('Choose an ad account.');
    if (!f.account.pageId) errs.push('Choose a Facebook Page.');
    if (!f.account.waNumber) errs.push('Choose a WhatsApp number.');
    (ctx.checks?.checks || [])
      .filter((c) => c.blocking && c.status !== 'pass')
      .forEach((c) => errs.push(c.status === 'unknown' ? `${c.label}: couldn't be checked (${c.message})` : c.message || c.label));
    return errs;
  },
  objective: () => [],
  placements: (f) =>
    f.placementMode === 'manual' && f.placements.length === 0 ? ['Pick at least one placement, or use Automatic.'] : [],
  ads: (f) => {
    const errs = [];
    if (f.ads.length < MIN_ADS) errs.push(`Add at least ${MIN_ADS} ads.`);
    if (f.ads.length > MAX_ADS) errs.push(`Use at most ${MAX_ADS} ads.`);
    f.ads.forEach((ad, i) => errs.push(...adErrors(ad, `Ad ${i + 1}`)));
    return errs;
  },
  targeting: (f) => {
    const t = f.targeting;
    const errs = [];
    if (t.locations.length === 0) errs.push('Add at least one location.');
    if (t.ageMin > t.ageMax) errs.push('Minimum age is above maximum age.');
    if (isLockedCategory(f.specialAdCategory) && t.locations.some((l) => l.type === 'zip')) {
      errs.push('Pincodes can\'t be used for loans, housing or jobs ads. Use a city or state instead.');
    }
    if (isLockedCategory(f.specialAdCategory)
      && t.locations.some((l) => (l.type === 'city' || l.type === 'custom') && (l.radiusKm || 0) < SPECIAL_CATEGORY_MIN_RADIUS_KM)) {
      errs.push(`For loans, housing or jobs ads, every city and dropped pin needs a radius of at least ${SPECIAL_CATEGORY_MIN_RADIUS_KM} km.`);
    }
    return errs;
  },
  budget: (f, ctx) => {
    const b = f.budget;
    const errs = [];
    const daily = Number(b.daily);
    if (!b.daily || !(daily > 0)) errs.push('Enter a daily budget.');
    else if (ctx.minDailyBudget && daily < ctx.minDailyBudget) {
      errs.push(`The daily budget must be at least ${ctx.currencySymbol}${ctx.minDailyBudget} for this ad account.`);
    }
    if (b.startDate && b.startDate < today()) errs.push('The start date is in the past.');
    if (b.endMode === 'date') {
      if (!b.endDate) errs.push('Pick an end date, or choose "Run until I pause it".');
      else if (b.endDate <= (b.startDate || today())) errs.push('The end date must be after the start date.');
    }
    return errs;
  },
  autofill: (f) => {
    const texts = f.autofillMode === 'all' ? [f.autofillAll] : f.ads.map((a) => a.autofill);
    return texts.some((t) => !t.trim())
      ? ['Add the autofill message' + (f.autofillMode === 'all' ? '.' : ' for every ad.')]
      : texts.some((t) => t.length > AUTOFILL_MAX) ? [`Autofill messages can be at most ${AUTOFILL_MAX} characters.`] : [];
  },
};

export const validateSection = (id, form, ctx) => VALIDATORS[id](form, ctx);

// { sectionId: [messages] } for every section with problems.
export const validateAll = (form, ctx) => Object.fromEntries(
  SECTIONS.map(({ id }) => [id, validateSection(id, form, ctx)]).filter(([, errs]) => errs.length),
);

// "nodebot:12" → { type: 'nodebot', id: 12 }; '' → null.
const chatbotPayload = (value) => {
  if (!value) return null;
  const [type, id] = value.split(':');
  return { type, id: Number(id) };
};

// The JSON the backend publish / validate endpoints receive. Retired interests are dropped here.
export const buildPayload = (form, { retiredIds = [], draftId = null } = {}) => {
  const t = form.targeting;
  const locked = isLockedCategory(form.specialAdCategory);
  return {
    draft_id: draftId,
    name: form.name.trim(),
    ad_account_id: form.account.adAccountId,
    page_id: form.account.pageId,
    wa_number_id: form.account.waNumberId,
    wa_number: form.account.waNumber,
    goal: form.goal,
    special_ad_category: form.specialAdCategory,
    placements: form.placementMode === 'manual' ? form.placements : [], // [] = Automatic
    targeting: {
      advantage_audience: t.advantageAudience,
      country: t.country,
      locations: t.locations.map(({ type, key, name, radiusKm, latitude, longitude }) => ({
        type, key, name, radius_km: radiusKm ?? null, latitude: latitude ?? null, longitude: longitude ?? null,
      })),
      age_min: locked ? LOCKED_AGE[0] : t.ageMin,
      age_max: locked ? LOCKED_AGE[1] : t.ageMax,
      gender: locked ? 'all' : t.gender,
      detailed: t.detailed.filter((d) => !retiredIds.includes(d.id)).map(({ id, name, type }) => ({ id, name, type })),
    },
    budget: {
      daily_budget: Number(form.budget.daily), // major units (₹), the backend converts to paise
      start_date: form.budget.startDate || null,
      end_date: form.budget.endMode === 'date' ? form.budget.endDate : null,
    },
    ads: form.ads.map((ad) => ({
      name: ad.name.trim(),
      format: ad.format,
      media_asset_id: ad.mediaAssetId,
      primary_text: ad.primaryText.trim(),
      headline: ad.headline.trim(),
      autofill_message: (form.autofillMode === 'all' ? form.autofillAll : ad.autofill).trim(),
      chatbot: chatbotPayload(form.chatbotMode === 'all' ? form.chatbotAll : ad.chatbot),
    })),
  };
};
